import React, { useState, useEffect } from 'react';
import { furnitureTypesApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  Package, Plus, Pencil, Trash2, X, Check, AlertCircle,
  ToggleLeft, ToggleRight, Search
} from 'lucide-react';
import DynamicIcon from '../components/DynamicIcon';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const FurnitureTypesPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    label: '',
    description: '',
    icon: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadTypes();
  }, [showInactive]);

  const loadTypes = async () => {
    try {
      setLoading(true);
      const response = await furnitureTypesApi.getAll(showInactive);
      setTypes(response.data);
    } catch (error) {
      console.error('Erreur chargement types:', error);
      toast.error('Erreur lors du chargement des types');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setFormData({ code: '', label: '', description: '', icon: '' });
    setEditingType(null);
    setFormError('');
    setShowDialog(true);
  };

  const openEditDialog = (type) => {
    setFormData({
      code: type.code,
      label: type.label,
      description: type.description || '',
      icon: type.icon || ''
    });
    setEditingType(type);
    setFormError('');
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    try {
      if (editingType) {
        await furnitureTypesApi.update(editingType.id, {
          label: formData.label,
          description: formData.description || null,
          icon: formData.icon || null
        });
        toast.success('Type de mobilier mis à jour');
      } else {
        await furnitureTypesApi.create(formData);
        toast.success('Type de mobilier créé');
      }
      setShowDialog(false);
      loadTypes();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      setFormError(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggle = async (type) => {
    try {
      await furnitureTypesApi.toggle(type.id);
      toast.success(`Type ${type.is_active ? 'désactivé' : 'activé'}`);
      loadTypes();
    } catch (error) {
      console.error('Erreur toggle:', error);
      toast.error('Erreur lors du changement de statut');
    }
  };

  const handleDelete = async (type) => {
    try {
      await furnitureTypesApi.delete(type.id);
      toast.success('Type de mobilier supprimé');
      setDeleteConfirm(null);
      loadTypes();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
      setDeleteConfirm(null);
    }
  };

  // Filter types by search
  const filteredTypes = types.filter(t => 
    t.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="furniture-types-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            Types de mobilier
          </h1>
          <p className="text-slate-500 mt-1">Gérez les catégories de mobilier</p>
        </div>
        {isAdmin && (
          <Button 
            onClick={openCreateDialog} 
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="add-type-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau type
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Rechercher un type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="show-inactive"
                checked={showInactive}
                onCheckedChange={setShowInactive}
              />
              <Label htmlFor="show-inactive" className="text-sm text-slate-600">
                Afficher les types inactifs
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Types Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-slate-400" />
            Types de mobilier ({filteredTypes.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
            </div>
          ) : filteredTypes.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucun type de mobilier trouvé</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Icône</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Libellé</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-center">Mobiliers</TableHead>
                  <TableHead className="text-center">Statut</TableHead>
                  {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTypes.map((type) => (
                  <TableRow key={type.id} className={!type.is_active ? 'opacity-50' : ''}>
                    <TableCell>
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 text-blue-600">
                        <DynamicIcon name={type.icon || type.code} className="w-4 h-4" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="bg-slate-100 px-2 py-1 rounded text-sm">{type.code}</code>
                    </TableCell>
                    <TableCell className="font-medium">{type.label}</TableCell>
                    <TableCell className="text-slate-500 max-w-xs truncate">
                      {type.description || '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">{type.furniture_count}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {type.is_active ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Actif</Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-400">Inactif</Badge>
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggle(type)}
                            title={type.is_active ? 'Désactiver' : 'Activer'}
                          >
                            {type.is_active ? (
                              <ToggleRight className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <ToggleLeft className="w-4 h-4 text-slate-400" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(type)}
                            title="Modifier"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteConfirm(type)}
                            className="text-rose-600 hover:text-rose-700"
                            title="Supprimer"
                            disabled={type.furniture_count > 0}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingType ? 'Modifier le type' : 'Nouveau type de mobilier'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-rose-50 text-rose-700 text-sm">
                <AlertCircle className="w-4 h-4" />
                {formError}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="code">Code *</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                placeholder="ex: bureau_direction"
                disabled={!!editingType}
                required
                data-testid="type-code-input"
              />
              <p className="text-xs text-slate-500">
                Identifiant unique (minuscules, underscores). Non modifiable après création.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="label">Libellé *</Label>
              <Input
                id="label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="ex: Bureau de direction"
                required
                data-testid="type-label-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description optionnelle..."
                data-testid="type-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="icon">Icône (optionnel)</Label>
              <Input
                id="icon"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                placeholder="ex: desk, armchair, archive..."
                data-testid="type-icon-input"
              />
              <p className="text-xs text-slate-500">
                Nom de l'icône Lucide (voir lucide.dev/icons)
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={formLoading}>
                {formLoading ? 'Enregistrement...' : (editingType ? 'Mettre à jour' : 'Créer')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Êtes-vous sûr de vouloir supprimer le type <strong>"{deleteConfirm?.label}"</strong> ?
          </p>
          <p className="text-sm text-slate-500">
            Cette action est irréversible.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Annuler
            </Button>
            <Button 
              className="bg-rose-600 hover:bg-rose-700"
              onClick={() => handleDelete(deleteConfirm)}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FurnitureTypesPage;
